/**
 * Shared floating-label helpers for HitInput, HitSelect, HitFastModeBrowser, …
 * Components must define `useFloatingLabel` (and usually override `hasFloatingValue`
 * when value semantics differ).
 *
 * When the field is erroneous and not focused:
 * - the validation message is shown lifted on the border (danger color)
 * - the normal field label stays resting inside the field (floating)
 * On focus, the error hides and the normal label lifts as usual.
 */

import {getValidationErrors} from '../../models/hit/common/validation';
import {i18n} from '../../plugins/i18n/i18n.js';

export default {
  inject: {
    hitGroupFloatingLabels: {
      default: null,
    },
    hitGroupForceChildInline: {
      default: null,
    },
  },
  computed: {
    isInsideFloatingHitGroup() {
      const h = this.hitGroupFloatingLabels;
      if (h == null) {
        return false;
      }
      return typeof h === 'function' ? h() : !!h;
    },
    isGroupForcingChildInline() {
      const h = this.hitGroupForceChildInline;
      if (h == null) {
        return false;
      }
      return typeof h === 'function' ? h() : !!h;
    },
    effectiveInlineInput() {
      if (this.isInsideFloatingHitGroup) {
        return false;
      }
      if (this.isGroupForcingChildInline) {
        return true;
      }
      return this.inlineInput;
    },
    baseInputLabel() {
      return this.useFloatingLabel ? null : this.label;
    },
    /**
     * Focus / open state used to decide whether the floating label is lifted
     * and whether an error message should be shown as the label.
     */
    isFloatingInteracted() {
      return !!(
        this.isFocussed ||
        this.selectHasFocus ||
        this.isDropdownVisible ||
        this.editorFocused
      );
    },
    floatingErrorMessage() {
      if (!this.useFloatingLabel || !this.erroneous) {
        return null;
      }
      if (this.isFloatingInteracted) {
        return null;
      }
      // includeSilent: match the danger border, which also uses $silentErrors
      // before the field has been focused/blurred ($dirty).
      const errors = getValidationErrors(this.validationState, {
        includeSilent: true,
      });
      if (!errors.length) {
        return null;
      }
      const first = errors[0];
      // Never call this.t here: setup()/useI18n() bindings are not reliably
      // available on Options API consumers of this mixin.
      if (!first?.translationKey) {
        return null;
      }
      if (typeof this.$t === 'function') {
        return this.$t(first.translationKey, first.params);
      }
      return i18n.global.t(first.translationKey, first.params);
    },
    showFloatingError() {
      return !!this.floatingErrorMessage;
    },
    /** Caption shown in the resting / lifted floating label (never the error). */
    floatingLabelText() {
      return this.label || this.placeholder || '';
    },
    /**
     * While an error chip is lifted on the border, keep the caption inside
     * the field unless a value is already present (then hide the caption).
     */
    showFloatingCaption() {
      if (!this.useFloatingLabel) {
        return false;
      }
      // Original:
      // if (this.showFloatingError && this.hasFloatingValue) {
      //   return false;
      // }
      // return true;
      //
      // Date/datetime: native placeholder (dd.mm.yyyy) + resting caption + error chip
      // stack and look garbled. Hide the inner caption whenever the error chip is shown.
      if (this.showFloatingError) {
        if (this.hasFloatingValue) {
          return false;
        }
        if (this.isDate || this.isDateTime) {
          return false;
        }
      }
      return true;
    },
    hasFloatingValue() {
      const v = this.value;
      if (v === null || v === undefined) {
        return false;
      }
      if (v === '') {
        return false;
      }
      if (typeof v === 'string' && v.trim() === '') {
        return false;
      }
      return true;
    },
    floatingLabelLifted() {
      if (!this.useFloatingLabel) {
        return false;
      }
      // Error on the border: keep the caption resting inside when empty
      if (this.showFloatingError) {
        return false;
      }
      return this.isFloatingInteracted || this.hasFloatingValue;
    },
    resolvedPlaceholder() {
      if (!this.useFloatingLabel) {
        return this.placeholder;
      }
      if (!this.label && this.placeholder) {
        return '';
      }
      if (!this.floatingLabelLifted) {
        return '';
      }
      return this.placeholder;
    },
    floatingLabelClassList() {
      return [
        this.floatingLabelLifted
          ? 'hit-floating-label--lifted'
          : 'hit-floating-label--rest',
        !this.floatingLabelLifted && (this.disabled || this.readonly)
          ? 'hit-floating-label--muted'
          : null,
      ];
    },
    /** Lifted error chip on the top border (same red as border-danger). */
    floatingErrorLabelClassList() {
      return [
        'hit-floating-label',
        'hit-floating-label--lifted',
        'hit-floating-label--error',
      ];
    },
    /** Inline so color wins over later muted / text-panel rules. */
    floatingErrorLabelStyle() {
      return {
        color: 'var(--color-danger)',
        '-webkit-text-fill-color': 'var(--color-danger)',
      };
    },
  },
};
